import React, { useState, useRef, useEffect } from 'react';

/**
 * LazyImage component with intersection observer for performance optimization
 * Supports placeholder, error handling, and fade-in animation
 */
const LazyImage = ({
  src,
  alt,
  className = '',
  placeholderSrc = null,
  errorSrc = '/images/placeholder-dashboard.png',
  fallbackSrc = null, // Extract fallbackSrc to prevent it from being passed to DOM
  threshold = 0.1,
  rootMargin = '50px',
  fadeInDuration = 300,
  onLoad = null,
  onError = null,
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  // Intersection Observer setup
  useEffect(() => {
    const currentImgRef = imgRef.current;
    
    if (!currentImgRef) return;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          setIsLoading(true);
          observerRef.current?.unobserve(currentImgRef);
        }
      },
      {
        threshold,
        rootMargin
      }
    );

    observerRef.current.observe(currentImgRef);

    return () => {
      if (observerRef.current && currentImgRef) {
        observerRef.current.unobserve(currentImgRef);
      }
    };
  }, [threshold, rootMargin]);

  // Handle image load
  const handleLoad = (event) => {
    setIsLoaded(true);
    setIsLoading(false);
    setHasError(false);
    onLoad?.(event);
  };

  // Handle image error
  const handleError = (event) => {
    setHasError(true);
    setIsLoading(false);
    setIsLoaded(false);
    onError?.(event);
  };

  // Determine which image source to use
  const getImageSrc = () => {
    if (hasError) return fallbackSrc || errorSrc;
    if (!isInView) return placeholderSrc;
    return src;
  };

  // Generate CSS classes for styling
  const getImageClasses = () => {
    const baseClasses = ['lazy-image'];
    
    if (className) baseClasses.push(className);
    if (isLoading) baseClasses.push('lazy-image--loading');
    if (isLoaded && !hasError) baseClasses.push('lazy-image--loaded');
    if (hasError) baseClasses.push('lazy-image--error');
    
    return baseClasses.join(' ');
  };

  return (
    <div className="lazy-image-container" style={{ position: 'relative' }}>
      {/* Loading indicator */}
      {isLoading && !isLoaded && (
        <div className="lazy-image-loading">
          <div className="loading-spinner"></div>
        </div>
      )}
      
      {/* Main image */}
      <img
        ref={imgRef}
        src={getImageSrc()}
        alt={alt}
        className={getImageClasses()}
        onLoad={handleLoad}
        onError={handleError}
        style={{
          opacity: isLoaded && !hasError ? 1 : 0.7,
          transition: `opacity ${fadeInDuration}ms ease-in-out`,
          ...props.style
        }}
        {...props}
      />
      
      {/* Error overlay */}
      {hasError && (
        <div className="lazy-image-error-overlay">
          <span className="error-text">Failed to load image</span>
        </div>
      )}
      
      <style>{`
        .lazy-image-container {
          display: inline-block;
          position: relative;
          overflow: hidden;
        }
        
        .lazy-image {
          display: block;
          width: 100%;
          height: auto;
        }
        
        .lazy-image--loading {
          filter: blur(2px);
        }
        
        .lazy-image--loaded {
          filter: none;
        }
        
        .lazy-image--error {
          filter: grayscale(100%);
        }
        
        .lazy-image-loading {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 1;
        }
        
        .loading-spinner {
          width: 24px;
          height: 24px;
          border: 2px solid #f3f3f3;
          border-top: 2px solid #3498db;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        
        .lazy-image-error-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2;
        }
        
        .error-text {
          color: #666;
          font-size: 12px;
          text-align: center;
          padding: 8px;
        }
        
        @keyframes spin {
          0% { transform: translate(-50%, -50%) rotate(0deg); }
          100% { transform: translate(-50%, -50%) rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default LazyImage;

// Hook for preloading images
export const useImagePreloader = (imageSources = []) => {
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [failedImages, setFailedImages] = useState(new Set());
  
  useEffect(() => {
    if (!imageSources.length) return;
    
    const preloadImage = (src) => {
      return new Promise((resolve) => {
        const img = new Image();
        
        img.onload = () => {
          setLoadedImages(prev => new Set([...prev, src]));
          resolve({ src, success: true });
        };
        
        img.onerror = () => {
          setFailedImages(prev => new Set([...prev, src]));
          resolve({ src, success: false });
        };
        
        img.src = src;
      });
    };
    
    // Preload all images
    Promise.allSettled(
      imageSources.map(src => preloadImage(src))
    ).then(results => {
      console.log('Image preloading completed:', results);
    });
  }, [imageSources]);
  
  return {
    loadedImages,
    failedImages,
    isImageLoaded: (src) => loadedImages.has(src),
    isImageFailed: (src) => failedImages.has(src)
  };
};

// Utility function to optimize image URLs
export const optimizeImageUrl = (url, options = {}) => {
  if (!url) return null;
  
  const {
    width = null,
    height = null,
    quality = 80,
    format = 'webp'
  } = options;
  
  // For Supabase storage URLs, add transformation parameters
  if (url.includes('supabase.co/storage')) {
    const urlObj = new URL(url);
    const params = new URLSearchParams();
    
    if (width) params.set('width', width.toString());
    if (height) params.set('height', height.toString());
    if (quality) params.set('quality', quality.toString());
    if (format) params.set('format', format);
    
    if (params.toString()) {
      urlObj.search = params.toString();
      return urlObj.toString();
    }
  }
  
  return url;
};